import { useEffect, useState } from 'react';
import * as yup from 'yup';

import { ILocationDecoded, lookupApiCallApi } from '@mopla/data-models';
import { Logger } from '@mopla/logger';
import { getLocationCategory } from '@mopla/utils';

const positionSchema = yup.object({
	lat: yup.number().required(),
	lng: yup.number().required(),
});

const lookupResponseSchema = yup.object({
	id: yup.string().required(),
	title: yup.string().required(),
	resultType: yup.string().notRequired(),
	position: positionSchema.required(),
	access: yup.array(positionSchema).notRequired(),
	categories: yup
		.array(
			yup.object({
				id: yup.string().required(),
				name: yup.string().optional(),
				primary: yup.boolean().optional(),
			})
		)
		.notRequired(),
	address: yup
		.object({
			city: yup.string().optional(),
			countryCode: yup.string().optional(),
			countryName: yup.string().optional(),
			county: yup.string().optional(),
			countyCode: yup.string().optional(),
			district: yup.string().optional(),
			houseNumber: yup.string().optional(),
			label: yup.string().optional(),
			postalCode: yup.string().optional(),
			state: yup.string().optional(),
			stateCode: yup.string().optional(),
			street: yup.string().optional(),
		})
		.notRequired(),
});

export const lookupById = async (
	locationId: string
): Promise<ILocationDecoded> => {
	try {
		const { data } = await lookupApiCallApi({ query: { id: locationId } });

		const validatedData = lookupResponseSchema.validateSync(data);

		const position = validatedData.access?.[0] || validatedData.position;

		return {
			id: validatedData.id,
			title: validatedData.title,
			position,
			address: validatedData.address ?? {},
			type: getLocationCategory(validatedData, 'address'),
		};
	} catch (error) {
		Logger.getInstance('useDecodeLocationById').error(
			'Error while decoding location',
			error
		);
		throw error;
	}
};

export const useDecodeLocationById = (locationId?: string) => {
	const [location, setLocation] = useState<ILocationDecoded | null>(null);

	useEffect(() => {
		if (!locationId) {
			return setLocation(null);
		}
		const lookupLocation = async () => {
			try {
				const result = await lookupById(locationId);
				setLocation(result ?? null);
			} catch {
				//Do nothing
			}
		};
		lookupLocation();
	}, [locationId]);

	return location;
};
